import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Activity, ArrowLeft, ArrowRight, Globe2, Images, LockKeyhole, MessageCircle, Search, Users, X } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import { Country } from 'country-state-city';
import { supabase } from '../supabaseClient';
import logo from '../assets/logo.png';
import './PublicCommunitiesDirectory.css';

const PAGE_SIZE = 24;
const APP_STORE_URL = 'https://apps.apple.com/ie/app/snows-proam/id6504996530';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.snowsproam.snowsproam';

const getInitial = name => String(name || 'C').trim().charAt(0).toUpperCase();
const COUNTRY_CODE_BY_NAME = new Map(Country.getAllCountries().map(item => [item.name.toLowerCase(), item.isoCode]));
const countryFlag = country => {
  const name = String(country || '').trim();
  const aliases = { usa: 'US', 'united states of america': 'US', england: 'GB', scotland: 'GB', wales: 'GB' };
  const code = /^[A-Za-z]{2}$/.test(name) ? name.toUpperCase() : aliases[name.toLowerCase()] || COUNTRY_CODE_BY_NAME.get(name.toLowerCase());
  return /^[A-Z]{2}$/.test(code || '')
    ? code.split('').map(letter => String.fromCodePoint(127397 + letter.charCodeAt(0))).join('')
    : '🌍';
};

export default function PublicCommunitiesDirectory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = React.useState(searchParams.get('q') || '');
  const [search, setSearch] = React.useState(searchParams.get('q') || '');
  const [rows, setRows] = React.useState([]);
  const [facets, setFacets] = React.useState({ countries: [], sports: [] });
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState('');
  const [gallery, setGallery] = React.useState(null);
  const [galleryIndex, setGalleryIndex] = React.useState(0);
  const [attempt, setAttempt] = React.useState(0);
  const requestId = React.useRef(0);
  const galleryDialogRef = React.useRef(null);
  const galleryTriggerRef = React.useRef(null);

  const kind = searchParams.get('view') === 'world-chats' ? 'world_chat' : 'community';
  const sport = searchParams.get('sport') || '';
  const country = searchParams.get('country') || '';
  const minimumMembers = Number(searchParams.get('members') || 0);

  const updateParams = React.useCallback((updates, replace = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined || value === 0 || value === '0') next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace });
  }, [searchParams, setSearchParams]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      const clean = searchInput.trim();
      if (clean !== search) setSearch(clean);
      if (clean !== (searchParams.get('q') || '')) updateParams({ q: clean });
    }, 320);
    return () => window.clearTimeout(timer);
  }, [search, searchInput, searchParams, updateParams]);

  React.useEffect(() => {
    let active = true;
    setFacets({ countries: [], sports: [] });
    (async () => {
      const { data, error: facetError } = await supabase.rpc('get_public_community_directory_facets', { p_kind: kind });
      if (!active || facetError) return;
      setFacets({
        countries: (data || []).filter(row => row.facet_type === 'country').map(row => row.facet_value),
        sports: (data || []).filter(row => row.facet_type === 'sport').map(row => row.facet_value),
      });
    })();
    return () => { active = false; };
  }, [kind]);

  const fetchRows = React.useCallback(async ({ append = false } = {}) => {
    const currentRequest = ++requestId.current;
    if (append) setLoadingMore(true);
    else { setLoading(true); setRows([]); }
    setError('');
    const offset = append ? rows.length : 0;
    try {
      const { data, error: queryError } = await supabase.rpc('get_public_community_directory_v2', {
        p_kind: kind,
        p_search: search || null,
        p_sport: sport || null,
        p_country: country || null,
        p_min_members: minimumMembers,
        p_limit: PAGE_SIZE,
        p_offset: offset,
      });
      if (queryError) throw queryError;
      if (currentRequest !== requestId.current) return;
      const nextRows = data || [];
      setRows(previous => append ? [...previous, ...nextRows] : nextRows);
      setTotal(Number(nextRows[0]?.total_count || (append ? total : 0)));
    } catch {
      if (currentRequest === requestId.current) setError('Communities could not be loaded. Check your connection and try again.');
    } finally {
      if (currentRequest === requestId.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [country, kind, minimumMembers, rows.length, search, sport, total]);

  React.useEffect(() => { fetchRows(); }, [attempt, country, kind, minimumMembers, search, sport]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const dialog = galleryDialogRef.current;
    if (!dialog) return;
    if (gallery && !dialog.open) dialog.showModal();
    if (!gallery && dialog.open) dialog.close();
  }, [gallery]);

  const openGallery = (row, trigger) => {
    const photos = Array.isArray(row.media_urls) ? row.media_urls.filter(Boolean) : [];
    if (!photos.length) return;
    galleryTriggerRef.current = trigger;
    setGalleryIndex(0);
    setGallery({ name: row.name, photos });
  };

  const closeGallery = () => {
    setGallery(null);
    window.requestAnimationFrame(() => galleryTriggerRef.current?.focus());
  };

  const selectKind = nextKind => {
    setSearchInput('');
    setSearch('');
    setSearchParams(nextKind === 'world_chat' ? { view: 'world-chats' } : {}, { replace: true });
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setSearchParams(kind === 'world_chat' ? { view: 'world-chats' } : {}, { replace: true });
  };

  const hasFilters = Boolean(search || sport || country || minimumMembers);
  const resultLabel = loading ? 'Finding communities…' : `${total.toLocaleString()} ${kind === 'world_chat' ? (total === 1 ? 'World Chat' : 'World Chats') : (total === 1 ? 'community' : 'communities')}`;

  return <main className="communities-directory-page">
    <svg className="communities-directory-track" viewBox="0 0 1200 1200" fill="none" aria-hidden="true">
      {[0, 1, 2, 3, 4, 5].map(index => <rect key={index} x={180 + index * 32} y={-160 + index * 32} width={820 - index * 64} height={1340 - index * 64} rx={410 - index * 32} stroke="currentColor" strokeWidth="3" />)}
    </svg>
    <header className="communities-directory-nav">
      <Link to="/" aria-label="Snows ProAm home"><img src={logo} alt="" /></Link>
      <span>WHERE ATHLETES BELONG</span>
      <Link to="/">Home <span aria-hidden="true">↗</span></Link>
    </header>

    <div className="communities-directory-shell">
      <section className="communities-directory-hero" aria-labelledby="communities-title">
        <div><p>COMMUNITIES</p><h1 id="communities-title">Find a community</h1></div>
        <p>Search by sport, country or community size.</p>
      </section>

      <section className="communities-directory-panel" aria-label="Find a community">
        <div className="communities-directory-tabs" role="tablist" aria-label="Community type">
          <button type="button" role="tab" aria-selected={kind === 'community'} onClick={() => selectKind('community')}><Users aria-hidden="true" /> Communities</button>
          <button type="button" role="tab" aria-selected={kind === 'world_chat'} onClick={() => selectKind('world_chat')}><Globe2 aria-hidden="true" /> World Chats</button>
        </div>

        <div className="communities-directory-intro-copy">
          <p>{kind === 'world_chat' ? 'Choose a country and sport. Your app profile determines which World Chats you can enter.' : 'Public communities are open to join. Private communities let you request access.'}</p>
          <span>{resultLabel}</span>
        </div>

        <form className="communities-directory-filters" onSubmit={event => event.preventDefault()}>
          <label className="communities-directory-search"><span>Search</span><div><Search aria-hidden="true" /><input type="search" value={searchInput} onChange={event => setSearchInput(event.target.value)} placeholder={kind === 'world_chat' ? 'Search country or sport' : 'Search communities'} autoComplete="off" />{searchInput && <button type="button" onClick={() => setSearchInput('')} aria-label="Clear search"><X aria-hidden="true" /></button>}</div></label>
          <label><span>Sport</span><select value={sport} onChange={event => updateParams({ sport: event.target.value })}><option value="">All sports</option>{facets.sports.map(value => <option key={value} value={value}>{value}</option>)}</select></label>
          <label><span>Country</span><select value={country} onChange={event => updateParams({ country: event.target.value })}><option value="">All countries</option>{facets.countries.map(value => <option key={value} value={value}>{value}</option>)}</select></label>
          <label><span>Community size</span><select value={minimumMembers} onChange={event => updateParams({ members: Number(event.target.value) })}><option value="0">Any size</option><option value="5">5+ members</option><option value="10">10+ members</option><option value="25">25+ members</option><option value="50">50+ members</option></select></label>
        </form>

        {hasFilters && <div className="communities-directory-filter-summary"><span>Showing results for your selected filters.</span><button type="button" onClick={clearFilters}>Clear all filters</button></div>}

        {error ? <div className="communities-directory-state" role="alert"><MessageCircle aria-hidden="true" /><h3>We couldn’t load communities</h3><p>{error}</p><button type="button" onClick={() => setAttempt(value => value + 1)}>Try again</button></div> : loading ? (
          <div className="communities-directory-grid" aria-label="Loading communities" aria-busy="true">{Array.from({ length: 6 }, (_, index) => <div className="communities-directory-skeleton" key={index} />)}</div>
        ) : rows.length === 0 ? <div className="communities-directory-state"><Search aria-hidden="true" /><h3>No matches yet</h3><p>Try a different search, sport, country or community size.</p>{hasFilters && <button type="button" onClick={clearFilters}>Clear all filters</button>}</div> : (
          <>
            <div className="communities-directory-grid">
              {rows.map(row => {
                const photoCount = Array.isArray(row.media_urls) ? row.media_urls.filter(Boolean).length : 0;
                return <article className="communities-directory-card" key={row.slug}>
                  <Link className="communities-directory-card-link" to={`/community/${row.slug}`} aria-label={`View ${row.name}${row.country ? ` in ${row.country}` : ''}`}>
                    <div className="communities-directory-card-art" aria-hidden="true"><span /><span /><span /></div>
                    <div className="communities-directory-card-logo">{kind === 'world_chat' ? <span className="communities-directory-card-flag" aria-hidden="true">{countryFlag(row.country)}</span> : row.image_url ? <img src={row.image_url} alt="" loading="lazy" /> : <span aria-hidden="true">{getInitial(row.name)}</span>}</div>
                    <div className="communities-directory-card-body">
                      <div className="communities-directory-card-type" aria-hidden={kind === 'world_chat' ? 'true' : undefined}>{kind === 'world_chat' ? <Globe2 aria-hidden="true" /> : row.private ? <><LockKeyhole aria-hidden="true" /> Private community</> : <><Users aria-hidden="true" /> Public community</>}</div>
                      <h3>{row.name}</h3>
                      {kind !== 'world_chat' && <p>{row.description || 'Connect, move and make progress together.'}</p>}
                      <div className="communities-directory-card-meta">{row.country && <span><span className="communities-directory-country-flag" aria-hidden="true">{countryFlag(row.country)}</span>{row.country}</span>}{row.sport_id && <span><Activity aria-hidden="true" />{row.sport_id}</span>}<span><Users aria-hidden="true" />{Number(row.member_count || 0).toLocaleString()} members</span></div>
                      <strong>View community <ArrowRight aria-hidden="true" /></strong>
                    </div>
                  </Link>
                  {kind !== 'world_chat' && photoCount > 0 && <button className="communities-directory-gallery-trigger" type="button" onClick={event => openGallery(row, event.currentTarget)} aria-label={`View ${photoCount} ${photoCount === 1 ? 'photo' : 'photos'} from ${row.name}`}><Images aria-hidden="true" /><span>{photoCount}</span></button>}
                </article>;
              })}
            </div>
            {rows.length < total && <div className="communities-directory-more"><button type="button" onClick={() => fetchRows({ append: true })} disabled={loadingMore}>{loadingMore ? 'Loading more…' : 'Show more communities'}</button></div>}
          </>
        )}
      </section>

      <section className="communities-directory-download" aria-label="Download Snows ProAm">
        <p>GET THE SNOWS PROAM APP</p><h2>Ready to join?</h2>
        <div><a href={APP_STORE_URL}><FontAwesomeIcon icon={faApple} aria-hidden="true" /><span><small>Download for</small><strong>iPhone</strong></span></a><a href={PLAY_STORE_URL}><FontAwesomeIcon icon={faAndroid} aria-hidden="true" /><span><small>Download for</small><strong>Android</strong></span></a></div>
      </section>

      <footer className="communities-directory-footer"><strong>EVERY ATHLETE. EVERY LEVEL.</strong><p>Community. Support. Progress.</p><Link to="/">Discover Snows ProAm ↗</Link></footer>
    </div>
    <dialog ref={galleryDialogRef} className="communities-directory-gallery" onCancel={event => { event.preventDefault(); closeGallery(); }} onClose={() => gallery && closeGallery()} onClick={event => { if (event.target === event.currentTarget) closeGallery(); }} aria-labelledby="community-gallery-title">
      {gallery && <div className="communities-directory-gallery-inner">
        <header><div><span>COMMUNITY PHOTOS</span><h2 id="community-gallery-title">{gallery.name}</h2></div><button type="button" onClick={closeGallery} aria-label="Close community photos"><X aria-hidden="true" /></button></header>
        <figure><img src={gallery.photos[galleryIndex]} alt={`${gallery.name}, ${galleryIndex + 1} of ${gallery.photos.length}`} /><figcaption>{galleryIndex + 1} / {gallery.photos.length}</figcaption></figure>
        {gallery.photos.length > 1 && <div className="communities-directory-gallery-controls"><button type="button" onClick={() => setGalleryIndex(index => (index - 1 + gallery.photos.length) % gallery.photos.length)} aria-label="Previous photo"><ArrowLeft aria-hidden="true" /> Previous</button><button type="button" onClick={() => setGalleryIndex(index => (index + 1) % gallery.photos.length)} aria-label="Next photo">Next <ArrowRight aria-hidden="true" /></button></div>}
      </div>}
    </dialog>
  </main>;
}
